/* API calls via react-query */

import { useQuery } from "@tanstack/react-query";

import { apiCall } from "./shared";

export function useGetOpportunities() {
  return useQuery({
    queryKey: ["opportunities"],
    queryFn: () => apiCall({ endpoint: "/cms/news" }),
    staleTime: 6000,
  });
}

export function useGetSyncs() {
  return useQuery({
    queryKey: ["syncs"],
    queryFn: () => apiCall({ endpoint: "/cms/syncs" }),
    staleTime: 6000,
  });
}

export function useGetBroadcasts({ ac, oc }) {
  return useQuery({
    queryKey: [["broadcasts"], ac, oc],
    queryFn: () => apiCall({ endpoint: `/broadcasts?ac=${ac}&oc=${oc}` }),
    manual: true,
    enabled: !!ac,
  });
}

export function useGetBroadcast({ ac, id }) {
  return useQuery({
    queryKey: ["broadcast", ac, id],
    queryFn: () => apiCall({ endpoint: `/broadcast?ac=${ac}&id=${id}` }),
    staleTime: 6000,
    enabled: !!ac && !!id,
  });
}

export function useGetSetlists({ ac, oc }) {
  return useQuery({
    queryKey: ["setlists", ac, oc],
    queryFn: () => apiCall({ endpoint: `/setlists?ac=${ac}&oc=${oc}` }),
    enabled: !!ac,
    manual: true,
  });
}

export function useGetGigClaims({
  ac,
  oc,
  performanceDate,
  venueFilter,
}) {
  return useQuery({
    queryKey: ["gigclaims", ac, oc, performanceDate, venueFilter],
    queryFn: () => apiCall({
      endpoint: `/gig-claims?ac=${ac}&oc=${oc}&performanceDate=${performanceDate}&venueFilter=${venueFilter}`,
    }),
    manual: true,
    enabled: !!ac,
  });
}

export function useGetSetlist({ ac, id }) {
  return useQuery({
    queryKey: ["setlist", ac, id],
    queryFn: () => apiCall({ endpoint: `/setlist?ac=${ac}&id=${id}` }),
    staleTime: 6000,
    enabled: !!ac && !!id,
  });
}

export function useGetGigClaim({ ac, id }) {
  return useQuery({
    queryKey: ["gigclaim", ac, id],
    queryFn: () => apiCall({ endpoint: `/gig-getclaim?ac=${ac}&id=${id}` }),
    manual: true,
    enabled: !!ac && !!id,
  });
}

export function useGetTheme({ cataloguecode }) {
  return useQuery({
    queryKey: ["theme", cataloguecode],
    queryFn: () => apiCall({ endpoint: `/cms/theme/${cataloguecode}` }),
    staleTime: 1000 * 60 * 60,
    enabled: !!cataloguecode,
  });
}

export function useGetThemeUnAuth() {
  return useQuery({
    queryKey: ["themeUnAuth"],
    queryFn: () => apiCall({ endpoint: "/cms/theme" }),
    staleTime: 1000 * 60 * 60,
  });
}

export function useGetMyAccounts() {
  return useQuery({
    queryKey: ["useGetMyAccounts"],
    queryFn: async () => {
      const results = await apiCall({
        endpoint: "/rightsapp/get",
        path: "/api/v1/artistaccount/getMyAccounts?pageSize=10000",
      });
      return results;
    },
    staleTime: 1000 * 60 * 60,
    retry: 1,
  });
}

export function useGetMyAccount({ artistaccountcode }) {
  return useQuery({
    queryKey: ["useGetMyAccount", artistaccountcode],
    queryFn: () => apiCall({
      endpoint: "/rightsapp/get",
      path: `/api/v1/artistaccount/getMyAccount?artistaccountcode=${artistaccountcode}`,
    }),
    staleTime: 1000 * 60 * 60,
    enabled: artistaccountcode !== null,
  });
}

export function useGetContracts({ artistaccountcode }) {
  return useQuery({
    queryKey: ["useGetContracts", artistaccountcode],
    queryFn: () => apiCall({ endpoint: `/contracts/account/${artistaccountcode}` }),
    staleTime: 1000 * 60 * 60,
    enabled: artistaccountcode !== null,
  });
}

export function useGetMyProfile() {
  return useQuery({
    queryKey: ["useGetMyProfile"],
    queryFn: () => apiCall({ endpoint: "/rightsapp/get", path: "/api/v1/user/getprofile" }),
    staleTime: 1000 * 60 * 60,
  });
}

export function useGetUserMeta() {
  return useQuery({
    queryKey: ["useGetUserMeta"],
    queryFn: () => apiCall({ endpoint: "/usermeta" }),
    staleTime: 1000 * 60 * 60,
    retry: 1,
  });
}

export function useGetArtistMeta({ aac }) {
  return useQuery({
    queryKey: ["useGetArtistMeta", aac],
    queryFn: () => apiCall({ endpoint: `/artist-meta?id=${aac}` }),
    staleTime: 1000 * 60 * 60,
    retry: 1,
    enabled: !!aac,
  });
}

export function useGetArtistISRCS({ aac }) {
  return useQuery({
    queryKey: ["getartistisrcs", aac],
    queryFn: () => apiCall({
      endpoint: "/rightsapp/get",
      path: `/api/v1/artistaccount/getmyaccountrecordingisrclist?artistaccountcode=${aac}`,
    }),
    staleTime: 1000 * 60 * 60,
    enabled: aac !== null,
  });
}

export function useGetSpotifySuggestions({ spotifyid, accountId }) {
  return useQuery({
    queryKey: ["getSpotifySuggestions", spotifyid, accountId],
    queryFn: () => apiCall({
      endpoint: `/spotify-suggestions?spotifyid=${spotifyid}&accountId=${accountId}`,
    }),
    enabled: !!spotifyid,
  });
}

export function useGetSuggestions({ spotifyid, accountId }) {
  return useQuery({
    queryKey: ["getSuggestions", spotifyid, accountId],
    queryFn: () => apiCall({
      endpoint: `/suggestions?spotifyid=${spotifyid}&accountId=${accountId}`,
    }),
    enabled: !!accountId,
  });
}

export function useGetMyCatalogues() {
  return useQuery({
    queryKey: ["getmycatalogues"],
    queryFn: () => apiCall({ endpoint: "/rightsapp/get", path: "/api/v1/catalogue/getmycatalogues" }),
    staleTime: 1000 * 60 * 60,
  });
}

export function useGetSocieties() {
  return useQuery({
    queryKey: ["getSocieties"],
    queryFn: () => apiCall({
      endpoint: "/rightsapp/get",
      path: "/api/v1/society/getsocieties?1000",
    }),
    staleTime: 1000 * 60 * 60,
  });
}

export function useGetMyWriters({ aac }) {
  return useQuery({
    queryKey: ["getWriters", aac],
    queryFn: () => apiCall({
      endpoint: "/rightsapp/get",
      path: `/api/v1/writer/getmywritersdetailed?pageSize=200&artistaccountcode=${aac}`,
    }),
    staleTime: 1000 * 60,
    enabled: !!aac,
  });
}

export function useGetMyWriter({ aac, wc }) {
  return useQuery({
    queryKey: ["getWriter", aac, wc],
    queryFn: async () => {
      const res = await apiCall({
        endpoint: "/rightsapp/get",
        path: `/api/v1/writer/getmywriter?accountcode=${aac}&writercode=${wc}`,
      });

      if (res.changespending) {
        const wa = await apiCall({
          endpoint: "/rightsapp/get",
          path: `/api/v1/writer/getmywriteramendment?accountcode=${aac}&writercode=${wc}`,
        });
        if (wa) {
          wa.etag = res.etag;
          return wa;
        }
      }

      return res;
    },
    staleTime: 0,
    enabled: !!aac && !!wc,
  });
}

export function useGetMySongs({ aac }) {
  return useQuery({
    queryKey: ["useGetMySongs", aac],
    queryFn: () => apiCall({
      endpoint: "/rightsapp/get",
      path: `/api/v1/work/getmyworks?pageSize=300&artistaccountcode=${aac}&sortby=title`,
    }),
    staleTime: 1000 * 60,
    enabled: !!aac,
    retry: 1,
  });
}

export function useGetMySongsDetailed({ aac, wc, includePerformers, includeRecordings, includeWorkSplits, includeAlternativeTitles }) {
  return useQuery({
    queryKey: ["getMySongsDetailed", aac, wc, includePerformers, includeRecordings, includeWorkSplits, includeAlternativeTitles],
    queryFn: async () => {
      let page = 1;
      const pageSize = 500;
      let basePath = `/api/v1/work/getmyworksdetailed?pageSize=${pageSize}&artistaccountcode=${aac}`;
      if (includePerformers) {
        basePath += `&includePerformers=true`;
      }
      if (includeRecordings) {
        basePath += `&includeRecordings=true`;
      }
      if (includeWorkSplits) {
        basePath += `&includeWorkSplits=true`;
      }
      if (includeAlternativeTitles) {
        basePath += `&includeAlternativeTitles=true`;
      }
      if (wc) {
        basePath += `&writerCode=${wc}`;
      }
      const result = await apiCall({
        endpoint: "/rightsapp/get-no-auto-pagination",
        path: `${basePath}&page=${page}`,
      });
      while (result.total > page * pageSize) {
        page += 1;

        const resultNew = await apiCall({
          endpoint: "/rightsapp/get-no-auto-pagination",
          path: `${basePath}&page=${page}`,
        });
        result.works = [...result.works, ...resultNew.works];
      }

      return result;
    },
    staleTime: 5000 * 60,
    enabled: !!aac,
    retry: false,
  });
}

export function useGetMySong({ aac, wc }) {
  return useQuery({
    queryKey: ["getSong", aac, wc],
    queryFn: async () => {
      const res = await apiCall({
        endpoint: "/rightsapp/get",
        path: `/api/v1/work/getmywork?accountcode=${aac}&workcode=${wc}`,
      });

      if (res?.changespending) {
        const wa = await apiCall({
          endpoint: "/rightsapp/get",
          path: `/api/v1/work/getmyworkamendment?artistaccountcode=${aac}&workcode=${wc}`,
        });
        if (wa) {
          wa.verifiedstate = res.verifiedstate;
          wa.changespending = res.changespending;
          return wa;
        }
      }
      return res;
    },
    staleTime: 0,
    enabled: !!aac && !!wc,
  });
}

export function useGetMyWorkAutoSuggest({ aac, searchstring }) {
  return useQuery({
    queryKey: ["getMyAutoSuggestedWorks", aac, searchstring],
    queryFn: () => apiCall({
      endpoint: "/rightsapp/get",
      path: `/api/v1/work/getmyautosuggestedworks?pageSize=300&artistaccountcode=${aac}&searchstring=${searchstring}`,
    }),
    staleTime: 1000 * 60,
    enabled: !!aac && !!searchstring,
  });
}

export function useGetTopWorks({ enabled } = {}) {
  return useQuery({
    queryKey: ["getTopWorks"],
    queryFn: async () => {
      const result = await apiCall({
        endpoint: "/rightsapp/get",
        path: "/api/v1/work/getmytopworks",
      });

      // Sort by createdDate (most recent first)
      if (result?.works) {
        result.works.sort((a, b) => {
          const dateA = new Date(a.createdDate);
          const dateB = new Date(b.createdDate);
          return dateB - dateA;
        });

        // Normalize camelCase API fields to lowercase to match the rest of the codebase
        result.works.forEach((work) => {
          work.artistaccountcode = work.accountCode ?? work.artistaccountcode;
          work.workcode = work.workCode ?? work.workcode;
          work.accountname = work.accountName ?? work.accountname;
          work.createddate = work.createdDate ?? work.createddate;
        });
      }

      return result?.works || [];
    },
    staleTime: 1000 * 60 * 60,
    enabled,
  });
}

export function useGetTopSongs({ accounts }) {
  return useQuery({
    queryKey: ["getTopSongs", accounts],
    queryFn: async () => {
      const promises = [];
      const t0 = performance.now();

      if (accounts) {
        const smallerGroup = JSON.parse(JSON.stringify(accounts)).slice(0, 100);

        smallerGroup.forEach((ac) => {
          promises.push(
            apiCall({
              endpoint: "/rightsapp/get-no-auto-pagination",
              path: `/api/v1/work/getmyworks?pageSize=10&page=1&artistaccountcode=${ac.artistaccountcode}&sortby=CreatedDate&sortAsc=false`,
            }).catch(() => console.log("error")),
          );
        });

        // &sortby=createddate&sortAsc=true
        const resultArray = [];

        const results = await Promise.allSettled(promises);
        results.forEach((result, i) => {
          const account = smallerGroup[i];

          if (result.status !== "fulfilled" || !result?.value?.works) {
            return;
          }
          result.value.works.forEach((r) => {
            r.cataloguecode = account.cataloguecode;
            r.artistaccountcode = account.artistaccountcode;
            r.accountname = account.accountname;
          });
          resultArray.push(result.value);
        });

        const t2 = performance.now();
        console.log(`Call to doSomething took ${t2 - t0} milliseconds.`);

        const combinedResults = resultArray.reduce(
          (a, b) => a.concat(b && b.works ? b.works : []),
          [],
        );

        // order by latest
        combinedResults.sort((a, b) => {
          const c = new Date(a.createddate);
          const d = new Date(b.createddate);
          return d - c;
        });

        return combinedResults;
      }
      return null;
    },
    staleTime: 1000 * 60 * 60,
    enabled: false,
  });
}

export function useGetMySongsDetailedPaginated({
  aac,
  wc,
  perPage,
  page,
  sortBy,
  desc,
  globalFilter,
}) {
  return useQuery({
    queryKey: [
      "getMySongsDetailedPaginated",
      aac,
      wc,
      perPage,
      page,
      sortBy,
      desc,
      globalFilter,
    ],
    queryFn: async () => {
      let sortByString = "";
      let sortAscString = "";
      let useSort = false;
      let filterString = "";
      if (sortBy) {
        sortByString = sortBy;
        sortAscString = desc ? "false" : "true";
        useSort = true;
      }

      if (sortByString === "title") {
        sortByString = "title";
      }
      if (sortByString === "createddate") {
        sortByString = "createddate";
      }
      if (sortByString === "verifiedstate") {
        sortByString = "verifiedstate";
      }

      if (globalFilter) {
        filterString += `&title=${globalFilter}&performingartistname=${globalFilter}`;
      }

      if (wc) {
        filterString += `&writerCode=${wc}`;
      }

      let result = null;
      if (useSort) {
        result = await apiCall({
          endpoint: "/rightsapp/get-no-auto-pagination",
          path: `/api/v1/work/getmyworksdetailed?pageSize=${perPage}&artistaccountcode=${aac}&page=${page}&sortBy=${sortByString}&sortAsc=${sortAscString}${filterString}`,
        });
      }
      else {
        result = await apiCall({
          endpoint: "/rightsapp/get-no-auto-pagination",
          path: `/api/v1/work/getmyworksdetailed?pageSize=${perPage}&artistaccountcode=${aac}&page=${page}${filterString}`,
        });
      }

      return result;
    },
    keepPreviousData: true,
    staleTime: 5000 * 60,
    enabled: !!aac,
    retry: false,
  });
}

export function useGetTopWriters({ accounts, enabled }) {
  return useQuery({
    queryKey: ["getTopWriters", accounts],
    queryFn: async () => {
      const promises = [];
      const t0 = performance.now();
      if (accounts) {
        const smallerGroup = JSON.parse(JSON.stringify(accounts)).slice(0, 100);

        smallerGroup.forEach((ac) => {
          promises.push(
            apiCall({
              endpoint: "/rightsapp/get-no-auto-pagination",
              path: `/api/v1/writer/getmywritersdetailed?pageSize=5&artistaccountcode=${ac.artistaccountcode}&sortby=CreatedDate&sortAsc=false`,
            }),
          );
        });
        const resultArray = [];
        const results = await Promise.allSettled(promises);

        results.forEach((result, i) => {
          const account = smallerGroup[i];
          if (result.status !== "fulfilled" || !result?.value?.writers) {
            return;
          }
          result.value.writers.forEach((r) => {
            r.cataloguecode = account.cataloguecode;
            r.artistaccountcode = account.artistaccountcode;
            r.accountname = account.accountname;
          });
          resultArray.push(result.value);
        });

        const t2 = performance.now();
        console.log(`Call to doSomething took ${t2 - t0} milliseconds.`);

        const combinedResults = resultArray.reduce(
          (a, b) => a.concat(b && b.writers ? b.writers : []),
          [],
        );

        // Sort verified writers (with a verifieddate) to the top, then by verifieddate desc.
        // Unverified writers (no verifieddate) sink to the bottom — the stat block uses
        // data[0] which will be the most recently verified writer.
        combinedResults.sort((a, b) => {
          if (a.verifieddate && !b.verifieddate)
            return -1;
          if (!a.verifieddate && b.verifieddate)
            return 1;
          return new Date(b.verifieddate).getTime() - new Date(a.verifieddate).getTime();
        });

        return combinedResults;
      }
      return null;
    },
    staleTime: 1000 * 60 * 60,
    enabled: enabled && !!accounts,
  });
}

export function useGetTopGigs({ accounts, enabled }) {
  return useQuery({
    queryKey: ["getTopGigsS", accounts],
    queryFn: async () => {
    // const t0 = performance.now();
      if (accounts) {
        const smallerGroup = JSON.parse(JSON.stringify(accounts)).slice(0, 100);

        const results = await apiCall({
          endpoint: `/top-gigs?acs=${smallerGroup.map(
            a => a.artistaccountcode,
          )}`,
        });

        const res = JSON.parse(JSON.stringify(results));

        res.forEach((result) => {
          const account = smallerGroup.find(
            s => s.artistaccountcode === result.account_code,
          );
          result.cataloguecode = account.cataloguecode;
          result.accountname = account.accountname;
        });

        return res;
      }
      return null;
    },
    staleTime: 1000 * 60 * 60,
    enabled: enabled && !!accounts,
  });
}

export function useGetSpotifyCount({ accounts, enabled }) {
  return useQuery({
    queryKey: ["getSpotifyCount", accounts],
    queryFn: async () => {
      if (accounts) {
        const smallerGroup = JSON.parse(JSON.stringify(accounts)).slice(0, 100);

        const results = await apiCall({
          endpoint: `/top-spotify?acs=${smallerGroup.map(
            a => a.artistaccountcode,
          )}`,
        });

        return results;
      }
      return null;
    },
    staleTime: 1000 * 60 * 60,
    enabled: enabled && !!accounts,
  });
}

export function useGetMyRecordingsDetailed({ aac, wc }) {
  return useQuery({
    queryKey: ["getRecordingsDetails", aac, wc],
    queryFn: () => apiCall({
      endpoint: "/rightsapp/get",
      path: `/api/v1/recording/getmyrecordingsdetailed?accountcode=${aac}&workcode=${wc}`,
    }),
    staleTime: 1000 * 60,
    enabled: !!aac && !!wc,
  });
}

export function useGetRecordingMetas({ id }) {
  return useQuery({
    queryKey: ["getRecordingMetas", id],
    queryFn: () => apiCall({ endpoint: `/recording-metas?id=${id}` }),
    staleTime: 1000 * 60,
    enabled: !!id,
  });
}

export function useGetMp3({ key }) {
  return useQuery({
    queryKey: ["getMp3", key],
    queryFn: () => apiCall({ endpoint: `/s3/put?key=${key}` }),
    staleTime: 1000 * 60,
    enabled: !!key,
  });
}

export function useGetMyStatements({ aac, previewstatements = false }) {
  return useQuery({
    queryKey: ["getMyStatements", aac, previewstatements],
    queryFn: () => apiCall({
      endpoint: "/rightsapp/get",
      path: `/api/v1/statement/getmyaccountstatements?artistaccountcode=${aac}&previewstatements=${previewstatements}`,
    }),
    staleTime: 1000 * 60 * 60,
    enabled: !!aac,
  });
}

export function useGetMyStatementFiles({ aac, suid }) {
  return useQuery({
    queryKey: ["getMyStatementFile", aac, suid],
    queryFn: () => apiCall({
      endpoint: "/rightsapp/get",
      path: `/api/v1/statement/getmyaccountstatementfiles?artistaccountcode=${aac}&statementuniqueidentifier=${suid}`,
    }),
    staleTime: 1000 * 60 * 10,
    enabled: !!aac && !!suid,
  });
}

export function useGetMyRecording({ id, aac, wc }) {
  return useQuery({
    queryKey: ["getRecording", id, aac, wc],
    queryFn: async () => {
      const result = await apiCall({
        endpoint: "/rightsapp/get",
        path: `/api/v1/recording/getmyrecording?recordingidentifier=${id}&artistaccountcode=${aac}&workcode=${wc}`,
      });
      return result;
    },
    staleTime: 1000 * 60,
    enabled: !!id && !!aac && !!wc,
  });
}

export function useGetMyRecordingAmendment({ id, aac, wc }) {
  return useQuery({
    queryKey: ["getRecordingAmendment", id, aac, wc],
    queryFn: async () => {
      const result = await apiCall({
        endpoint: "/rightsapp/get",
        path: `/api/v1/recording/getmyrecordingamendment?recordingamendmentidentifier=${id}&artistaccountcode=${aac}&workcode=${wc}`,
      });
      return result;
    },
    staleTime: 1000 * 60,
    enabled: !!id && !!aac && !!wc,
  });
}

export function useGetGenerateRecordingAttachmentPresignedUrl({
  id,
  aac,
  wc,
  fn,
  rt,
  ct,
}) {
  return useQuery({
    queryKey: ["generateRecordingAttachmentPresignedUrl", id, aac, wc, fn, rt, ct],
    queryFn: async () => {
      const result = await apiCall({
        endpoint: "/rightsapp/get",
        path: `/api/v1/recording/generaterecordingattachmentpresignedurl?identifier=${id}&artistaccountcode=${aac}&workcode=${wc}&filename=${fn}&recordingtype=${rt}&contenttype=${ct}`,
      });
      return result;
    },
    staleTime: 1000 * 60,
    enabled: !!id && !!aac && !!wc && !!fn && !!rt && !!ct,
  });
}

export function useGetKycStatus() {
  return useQuery({
    queryKey: ["getkyc"],
    queryFn: async () => {
      const result = await apiCall({ endpoint: "/rightsapp/get", path: "/api/v1/user/getkyc" });
      return result;
    },
    // will run on evey page load so need to be careful where to use this
    refetchOnMount: true,
  });
}

export function useGetSpotifyArtistDetail({ spotifyId }) {
  return useQuery({
    queryKey: ["getSpotifyArtistDetail", spotifyId],
    queryFn: async () => {
      const result = await apiCall({ endpoint: "/rightsapp/get", path: `/api/v1/spotify/artist/${spotifyId}` });
      return result.artist;
    },
    staleTime: 1000 * 60,
    enabled: !!spotifyId,
  });
}

/**
 * Fetches album suggestions from local database for a given Spotify artist ID
 * @param {string} spotifyid - The Spotify artist ID
 * @returns {UseQueryResult} React Query result with album suggestions
 */
export function useGetAlbumSuggestions(spotifyid) {
  return useQuery({
    queryKey: ["album-suggestions", spotifyid],
    queryFn: () => apiCall({ endpoint: `/album-suggestions?spotifyid=${spotifyid}` }),
    enabled: !!spotifyid,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}
